import { useState, useMemo, useCallback } from "react";


const MESES_ORDER = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];


const parsearFechaCarpeta = (nombre) => {
  const yearMatch = nombre.match(/(\d{4})/);
  if (!yearMatch) return null;
  const year = parseInt(yearMatch[1], 10);

  const nombreNorm = nombre.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  let minMes = 12;
  for (let i = 0; i < MESES_ORDER.length; i++) {
    const mesNorm = MESES_ORDER[i].normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (nombreNorm.includes(mesNorm)) {
      minMes = Math.min(minMes, i);
    }
  }
  if (minMes === 12) return null;
  return { year, mesIndex: minMes };
};


const aplicarOrdenVisualActas = (items) => {
  const carpetas = items.filter((i) => i.type === 'folder');
  const otros = items.filter((i) => i.type !== 'folder');

  const carpetasOrdenadas = [...carpetas].sort((a, b) => {
    const fA = parsearFechaCarpeta(a.name);
    const fB = parsearFechaCarpeta(b.name);
    if (!fA && !fB) return a.name.localeCompare(b.name, 'es');
    if (!fA) return 1;
    if (!fB) return -1;
    if (fA.year !== fB.year) return fA.year - fB.year;
    return fA.mesIndex - fB.mesIndex;
  });

  const carpetasConPrefijo = carpetasOrdenadas.map((item, index) => {
    const prefijo = String(index + 1).padStart(2, '0');
    // Eliminar prefijo anterior si ya existía (ej: "01. Actas ..." → "Actas ...")
    const nombreBase = item.name
      .replace(/^\d+\.\s*/, '')           // quita "01. "
      .replace(/^Actas\s+/i, '');         // quita "Actas " previo para no duplicar

    const nuevoViewName = `${prefijo}. Actas ${nombreBase}`;
    return { ...item, viewName: nuevoViewName };
  });

  return [...carpetasConPrefijo, ...otros];
};

export const esContextoActas = (codigo_tarea, filtro) => {
  // Por codigo_tarea (flujo normal)
  if (codigo_tarea) {
    const ct = codigo_tarea?.codigo_tarea ?? codigo_tarea;
    const codActividad = ct?.codActividad;
    const codigoTarea = ct?.codigo_tarea;
    const indicadorKey = ct?.key ?? ct?.datoIndicador?.key;
    return (
      codActividad === '2.1' &&
      codigoTarea === '2.1.4' &&
      indicadorKey === 'indicador_30'
    );
  }
  // Por filtro (flujo POA preliminar)
  if (filtro) {
    return (
      filtro?.actividad === '2.1' &&
      filtro?.tarea === '214' &&
      filtro?.indicador === '30'
    );
  }
  return false;
};

export function useFolderNavigation(
  initialData,
  initialNavigationState = null,
  // Nuevos parámetros opcionales para el contexto de actas
  codigo_tarea = null,
  filtro = null,
  // Ruta relativa (fs) de la carpeta raíz que se está navegando, usada para
  // que las acciones de la barra de herramientas (crear/descargar carpeta
  // actual) sepan sobre qué carpeta operar cuando el usuario está en "Inicio".
  rootPath = null,
) {
  const [rootData, setRootData] = useState(initialData);
  const [searchFilter, setSearchFilter] = useState("");

  // rootPath normalizado (sin slash final) para que sea consistente con
  // item.path, que nunca trae slash final.
  const normalizedRootPath = useMemo(
    () => (rootPath ? rootPath.replace(/\/$/, "") : rootPath),
    [rootPath]
  );

  // Precalcular si aplica el ordenamiento visual de actas
  const aplicarOrdenActas = useMemo(
    () => esContextoActas(codigo_tarea, filtro),
    [codigo_tarea, filtro]
  );

  const [navigationState, setNavigationState] = useState(
    initialNavigationState || {
      currentPath: [],
      currentItems: initialData,
      breadcrumbs: [{ name: "Inicio", path: [], folderPath: normalizedRootPath }],
    }
  );

  // Función para actualizar los datos manteniendo la navegación
  const updateData = useCallback((newData) => {
    setRootData(newData);

    if (navigationState.currentPath.length === 0) {
      setNavigationState(prev => ({
        ...prev,
        currentItems: newData
      }));
      return;
    }

    const findCurrentItemsByPath = (data, path) => {
      let currentLevel = data;
      for (const key of path) {
        const foundItem = currentLevel.find(item => item.key === key);
        if (!foundItem || !foundItem.children) return null;
        currentLevel = foundItem.children;
      }
      return currentLevel;
    };

    const newCurrentItems = findCurrentItemsByPath(newData, navigationState.currentPath);

    if (newCurrentItems) {
      setNavigationState(prev => ({
        ...prev,
        currentItems: newCurrentItems
      }));
    } else {
      let validPath = [];
      let validItems = newData;

      for (let i = 0; i < navigationState.currentPath.length; i++) {
        const testPath = navigationState.currentPath.slice(0, i + 1);
        const testItems = findCurrentItemsByPath(newData, testPath);
        if (testItems) {
          validPath = testPath;
          validItems = testItems;
        } else {
          break;
        }
      }

      const validBreadcrumbs = [{ name: "Inicio", path: [], folderPath: normalizedRootPath }];
      let currentLevel = newData;

      for (let i = 0; i < validPath.length; i++) {
        const pathKey = validPath[i];
        const foundItem = currentLevel.find(item => item.key === pathKey);
        if (foundItem) {
          validBreadcrumbs.push({
            name: foundItem.viewName || foundItem.name,
            path: validPath.slice(0, i + 1),
            folderPath: foundItem.path,
          });
          currentLevel = foundItem.children || [];
        }
      }

      setNavigationState({
        currentPath: validPath,
        currentItems: validItems,
        breadcrumbs: validBreadcrumbs,
      });
    }
  }, [navigationState.currentPath, normalizedRootPath]);

  // Items filtrados por búsqueda. Si aplica el contexto de actas,
  // se aplica el ordenamiento y renombre visual ANTES del filtro.
  // ⚠️ Solo se aplica cuando los items son carpetas de MESES (level >= 3).
  // Los proyectos (level 2) no deben recibir el prefijo "Actas".
  const filteredItems = useMemo(() => {
    const currentItems = navigationState.currentItems;

    // Detectar si los items actuales son meses: tienen fechas parseables
    // o su level es >= 3. Usamos el level del primer item como indicador.
    const esCarpetaMeses =
      aplicarOrdenActas &&
      currentItems.length > 0 &&
      (currentItems[0].level >= 3 || parsearFechaCarpeta(currentItems[0].name) !== null);

    const itemsBase = esCarpetaMeses
      ? aplicarOrdenVisualActas(currentItems)
      : currentItems;

    if (!searchFilter.trim()) return itemsBase;

    return itemsBase.filter(
      (item) =>
        item.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        (item.viewName && item.viewName.toLowerCase().includes(searchFilter.toLowerCase()))
    );
  }, [navigationState.currentItems, searchFilter, aplicarOrdenActas]);

  const navigateToFolder = useCallback((item) => {
    if (item.children && item.children.length > 0) {
      const newPath = [...navigationState.currentPath, item.key];
      const newBreadcrumbs = [
        ...navigationState.breadcrumbs,
        { name: item.viewName || item.name, path: newPath, folderPath: item.path },
      ];
      setNavigationState({
        currentPath: newPath,
        currentItems: item.children,
        breadcrumbs: newBreadcrumbs,
      });
    }
  }, [navigationState.currentPath, navigationState.breadcrumbs]);

  const navigateBack = useCallback(() => {
    if (navigationState.currentPath.length === 0) return;

    const newPath = navigationState.currentPath.slice(0, -1);
    const newBreadcrumbs = navigationState.breadcrumbs.slice(0, -1);

    let items = rootData;
    for (const pathKey of newPath) {
      const foundItem = items.find((item) => item.key === pathKey);
      if (foundItem?.children) items = foundItem.children;
    }
    setNavigationState({
      currentPath: newPath,
      currentItems: items,
      breadcrumbs: newBreadcrumbs,
    });
  }, [navigationState.currentPath, navigationState.breadcrumbs, rootData]);

  const navigateToBreadcrumb = useCallback((targetPath) => {
    let items = rootData;
    for (const pathKey of targetPath) {
      const foundItem = items.find((item) => item.key === pathKey);
      if (foundItem?.children) items = foundItem.children;
    }

    const breadcrumbIndex = navigationState.breadcrumbs.findIndex(
      (b) => JSON.stringify(b.path) === JSON.stringify(targetPath)
    );

    setNavigationState({
      currentPath: targetPath,
      currentItems: items,
      breadcrumbs: navigationState.breadcrumbs.slice(0, breadcrumbIndex + 1),
    });
  }, [navigationState.breadcrumbs, rootData]);

  const updateFileCount = useCallback((itemKey, newCount) => {
    const updateItemRecursively = (items) => {
      return items.map((item) => {
        if (item.key === itemKey) return { ...item, fileCount: newCount };
        if (item.children) return { ...item, children: updateItemRecursively(item.children) };
        return item;
      });
    };

    const updatedData = updateItemRecursively(rootData);
    setRootData(updatedData);

    const updatedCurrentItems = updateItemRecursively(navigationState.currentItems);
    setNavigationState((prev) => ({ ...prev, currentItems: updatedCurrentItems }));
  }, [rootData, navigationState.currentItems]);

  // Ruta relativa (fs) de la carpeta que se está navegando actualmente, para
  // que las acciones de la barra de herramientas (crear/descargar carpeta
  // actual) sepan sobre qué carpeta operar.
  const currentFolderPath =
    navigationState.breadcrumbs[navigationState.breadcrumbs.length - 1]?.folderPath ??
    normalizedRootPath;

  return {
    navigationState,
    filteredItems,
    searchFilter,
    setSearchFilter,
    navigateToFolder,
    navigateBack,
    navigateToBreadcrumb,
    canNavigateBack: navigationState.currentPath.length > 0,
    updateFileCount,
    updateData,
    rootData,
    currentFolderPath,
  };
}